import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { Button, Card, TextField } from "@atlas/ui/web";

/**
 * Troca da PRÓPRIA senha de quem está logado, em "Minha conta".
 *
 * Compartilhado porque a tela é a mesma nos dois portais que a usam — banco e
 * organização. Duas cópias divergiriam no primeiro ajuste de texto ou de regra
 * (o mínimo de caracteres, por exemplo), e quem revisasse uma não saberia da
 * outra. Quem varia é só o `trocar` e o `minimo`.
 *
 * Fica FECHADO por padrão, atrás de um botão: trocar a senha é ação ocasional, e
 * três campos de senha abertos numa tela de configuração convidam ao engano.
 * Mesmo padrão do "Redefinir senha" na ficha do sub-usuário.
 *
 * O servidor exige a senha atual — sem isso, uma sessão sequestrada trocaria a
 * senha e expulsaria o dono.
 */
export function TrocarMinhaSenha({
  trocar,
  minimo = 6,
  descricao = "Troque a senha que você usa para entrar. Só você precisa conhecê-la.",
}: {
  /** Chamada que troca a senha. Recebe a atual e a nova. */
  trocar: (senhaAtual: string, novaSenha: string) => Promise<unknown>;
  /** Mínimo de caracteres exigido pela API deste perfil. */
  minimo?: number;
  descricao?: string;
}) {
  const [atual, setAtual] = useState("");
  const [nova, setNova] = useState("");
  const [nova2, setNova2] = useState("");
  const [erro, setErro] = useState<string | null>(null);
  const [ok, setOk] = useState(false);
  const [aberto, setAberto] = useState(false);

  function fechar() {
    setAberto(false);
    setAtual(""); setNova(""); setNova2("");
    setErro(null);
  }

  const mut = useMutation({
    mutationFn: () => trocar(atual, nova),
    onSuccess: () => {
      setOk(true);
      fechar();
      setTimeout(() => setOk(false), 6000);
    },
    onError: (e) => setErro(e instanceof Error ? e.message : "Não foi possível trocar a senha."),
  });

  function submit(e: React.FormEvent) {
    e.preventDefault();
    setErro(null);
    setOk(false);
    if (nova.length < minimo) { setErro(`A nova senha deve ter no mínimo ${minimo} caracteres.`); return; }
    if (nova !== nova2) { setErro("As senhas não conferem."); return; }
    if (nova === atual) { setErro("A nova senha precisa ser diferente da atual."); return; }
    mut.mutate();
  }

  return (
    <Card style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <div>
        <div style={{ fontSize: 15, fontWeight: 700 }}>Minha senha</div>
        <div style={{ fontSize: 13, color: "var(--text-muted)" }}>{descricao}</div>
      </div>
      {/* A confirmação fica ACIMA do formulário para continuar visível depois de
          ele fechar — senão o "Senha alterada." sumiria junto com o sucesso. */}
      {ok ? <div style={{ color: "var(--emerald-500)", fontSize: 13 }}>Senha alterada.</div> : null}

      {!aberto ? (
        <div>
          <Button variant="ghost" onClick={() => { setAberto(true); setOk(false); }}>
            Alterar senha
          </Button>
        </div>
      ) : (
        <form onSubmit={submit} style={{ display: "flex", flexDirection: "column", gap: 12, maxWidth: 420 }}>
          <TextField
            label="Senha atual"
            type="password"
            value={atual}
            onChange={(e) => setAtual(e.target.value)}
            autoComplete="current-password"
            required
          />
          <TextField
            label="Nova senha"
            type="password"
            value={nova}
            onChange={(e) => setNova(e.target.value)}
            autoComplete="new-password"
            hint={`Mínimo ${minimo} caracteres.`}
            required
          />
          <TextField
            label="Confirmar nova senha"
            type="password"
            value={nova2}
            onChange={(e) => setNova2(e.target.value)}
            autoComplete="new-password"
            required
          />
          {erro ? <div style={{ color: "var(--danger-500)", fontSize: 13 }}>{erro}</div> : null}
          <div style={{ display: "flex", gap: 8 }}>
            <Button type="submit" disabled={mut.isPending}>
              {mut.isPending ? "Salvando..." : "Trocar senha"}
            </Button>
            {/* Cancelar LIMPA os campos: senha digitada nao fica pendente na tela. */}
            <Button variant="ghost" type="button" onClick={fechar} disabled={mut.isPending}>
              Cancelar
            </Button>
          </div>
        </form>
      )}
    </Card>
  );
}
